import { TranslateService } from '@ngx-translate/core';

import { ApiFragment } from '../entities/api-fragment.entity';
import { FormatBadge } from '../entities/stuk-formats.entity';
import { toRecordFormat } from './record-format.helper';

/** The roles a representatie can play, in the order the representaties overview lists them. */
const ROLE_LABELS: ReadonlyArray<{ flag: 'IsOriginal' | 'IsAccess' | 'IsPreservation'; key: string }> = [
    { flag: 'IsOriginal', key: 'hierarchy.formats.roles.original' },
    { flag: 'IsAccess', key: 'hierarchy.formats.roles.access' },
    { flag: 'IsPreservation', key: 'hierarchy.formats.roles.preservation' }
];

/**
 * One badge per distinct format the given representaties are in, keeping the order they came back in. A
 * representatie whose format cannot be derived at all contributes nothing rather than an empty badge — so a
 * stuk with only such representaties reads the same as one with none.
 *
 * HFBD-75: shared by the two derivations behind the `Formaten` column, so it means one thing everywhere. A
 * stuk is handed the representaties found for it by {@link StukFormatsService}; a representatie row is handed
 * itself, since one representatie *is* exactly one format.
 */
export function toFormatBadges(representations: ApiFragment[], translate: TranslateService): FormatBadge[] {
    const analogLabel: string = translate.instant('hierarchy.formats.analog');
    const byFormat: Map<string, { roles: Set<string>; isAccess: boolean }> = new Map();

    representations.forEach(representation => {
        const label: string | null = toRecordFormat(representation, analogLabel);

        if (!label) {
            return;
        }

        const entry: { roles: Set<string>; isAccess: boolean } = byFormat.get(label) ?? {
            roles: new Set<string>(),
            isAccess: false
        };
        rolesOf(representation, translate).forEach(role => entry.roles.add(role));
        entry.isAccess = entry.isAccess || representation.metadata.Administrative?.IsAccess === true;
        byFormat.set(label, entry);
    });

    return [...byFormat].map(([label, entry]) => ({ label, roles: [...entry.roles], isAccess: entry.isAccess }));
}

export function rolesOf(representation: ApiFragment, translate: TranslateService): string[] {
    return ROLE_LABELS.filter(role => representation.metadata.Administrative?.[role.flag] === true).map(role =>
        translate.instant(role.key)
    );
}
